import { useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE_URL, API_CONFIG } from '../config/api';
import socketService from '../services/socketService';

export const useChat = (user, userRole, isOpen) => {
  const [contacts, setContacts] = useState([]);
  const [unreadCounts, setUnreadCounts] = useState({});
  const [onlineUsers, setOnlineUsers] = useState(new Set());
  const [loading, setLoading] = useState(false);

  // Fetch contacts
  const fetchContacts = async () => {
    try {
      setLoading(true);
      const endpoint = `/messages/contacts/${user.user_id}?role=${userRole}`;
      const response = await axios.get(`${API_BASE_URL}${endpoint}`, API_CONFIG);
      setContacts(response.data || []);
    } catch (error) {
      console.error('Error fetching contacts:', error);
      setContacts([]);
    } finally {
      setLoading(false);
    }
  };

  // Fetch unread counts
  const fetchUnreadCounts = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/messages/unread/${user.user_id}`, API_CONFIG);
      const counts = {};
      response.data.unread_by_sender.forEach(item => {
        counts[item.sender_id] = item.unread_count;
      });
      setUnreadCounts(counts);
    } catch (error) {
      console.error('Error fetching unread counts:', error);
    }
  };

  // Mark messages as read
  const markAsRead = async (senderId) => {
    try {
      await axios.put(`${API_BASE_URL}/messages/read`, {
        sender_id: senderId,
        receiver_id: user.user_id
      }, API_CONFIG);
      
      // Update unread counts
      setUnreadCounts(prev => ({
        ...prev,
        [senderId]: 0
      }));
    } catch (error) {
      console.error('Error marking messages as read:', error);
    }
  };

  // Initialize socket connection and fetch data when chat opens
  useEffect(() => {
    if (isOpen && user) {
      fetchContacts();
      fetchUnreadCounts();
      
      // Connect to socket
      socketService.connect({
        user_id: user.user_id,
        role: userRole,
        mmda_id: user.mmda_id
      });
    }
  }, [isOpen, user, userRole]);

  // Socket event listeners
  useEffect(() => {
    if (!isOpen) return;

    const handleUserStatus = (userId, isOnline) => {
      setOnlineUsers(prev => {
        const newSet = new Set(prev);
        if (isOnline) {
          newSet.add(userId);
        } else {
          newSet.delete(userId);
        }
        return newSet;
      });
    };

    const handleNewMessage = (message, isSent = false) => {
      // Update unread counts if message received
      if (!isSent) {
        fetchUnreadCounts();
      }
    };

    const removeStatusListener = socketService.addStatusListener(handleUserStatus);
    const removeMessageListener = socketService.addMessageListener(handleNewMessage);

    return () => {
      removeStatusListener();
      removeMessageListener();
    };
  }, [isOpen]);

  return {
    contacts,
    unreadCounts,
    onlineUsers,
    loading,
    fetchContacts,
    fetchUnreadCounts,
    markAsRead
  };
};

export const useTotalUnreadCount = (user) => {
  const [totalUnread, setTotalUnread] = useState(0);

  const fetchUnreadCount = async () => {
    if (!user) return;
    
    try {
      const response = await axios.get(`${API_BASE_URL}/messages/unread/${user.user_id}`, API_CONFIG);
      setTotalUnread(response.data.total_unread || 0);
    } catch (error) {
      console.error('Error fetching unread count:', error);
    }
  };

  useEffect(() => {
    if (user) {
      fetchUnreadCount();
      // Refresh unread count every 30 seconds
      const interval = setInterval(fetchUnreadCount, 30000);
      return () => clearInterval(interval);
    }
  }, [user]);

  return { totalUnread, fetchUnreadCount };
};
